<script setup lang="ts">
import { Info } from 'lucide-vue-next';
import { useId } from 'vue';

type CheckboxProps = {
  modelValue: boolean;
  label: string;
  error?: string | null;
  link?: string;
};

type CheckboxEmits = {
  (event: 'update:modelValue', value: boolean): void;
};

defineProps<CheckboxProps>();
const emits = defineEmits<CheckboxEmits>();
const inputId = useId();

function handleChange(event: Event) {
  const target = event.target as HTMLInputElement;
  emits('update:modelValue', target.checked);
}
</script>

<template>
  <div class="base-checkbox-container">
    <label
      :for="inputId"
      class="base-checkbox-wrapper"
      :class="{ 'error-state': !!error }"
      :style="link ? '' : 'user-select: none;'"
    >
      <input
        v-bind="$attrs"
        :id="inputId"
        type="checkbox"
        :checked="modelValue"
        :required="!!$attrs.required"
        :aria-required="!!$attrs.required"
        :aria-labelledby="inputId"
        :aria-invalid="!!$attrs['aria-invalid']"
        class="base-checkbox-input"
        @change="handleChange"
      >

      <span class="base-checkbox-label">
        <span v-if="!!$attrs.required" aria-hidden="true" class="required-star">*</span>
        {{ label }}
      </span>
      <span v-if="link"><a :href="link" target="_blank"><Info class="link_icon" /></a></span>
      <span v-if="error" class="base-checkbox-error">{{ error }}</span>
    </label>
  </div>
</template>

<style scoped>
.base-checkbox-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
}

.base-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.base-checkbox-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.25rem;
}

.base-checkbox-input {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  width: 1.25rem;
  height: 1.25rem;
  min-width: 1.25rem;
  min-height: 1.25rem;

  border: 1px solid #d1d5db;
  background-color: white;
  border-radius: 0.25rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  background-size: 80%;
  background-repeat: no-repeat;
  background-position: center;
}

.base-checkbox-input:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
}

.base-checkbox-input:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 2px var(--yb-color-primary),
    0 0 0 4px var(--yb-color-primary);
  border-color: var(--yb-color-primary);
}

.base-checkbox-input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.base-checkbox-error {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--yb-color-error);
  text-align: left;
  display: block;
  margin-left: calc(1.25rem + 0.75rem);
}

.required-star {
  color: var(--yb-color-error);
  margin-left: 0.25rem;
  font-weight: 700;
}

.link_icon {
  position: relative;
  top: 3px;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
  padding: 4px;
  border: 1px solid var(--yb-color-primary);
  border-radius: 0.25rem;
  color: var(--yb-color-primary);
  cursor: pointer;
  &:hover {
    color: var(--yb-color-dark-primary);
    border: 1px solid var(--yb-color-dark-primary);
  }
}
</style>
